import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { ClickMarketTrendsLink_ForYouHeuristic } from '@sorare/core/src/protos/events/platform/web/events';

import { PlayerImg } from '../PlayerRecommendation/PlayerImg';
import { PlayerRecommendationRow } from '../PlayerRecommendationRow';
import { StarBallExponent } from '../RecommendationTitle/StarBallExponent';
import { SubstituteChip } from '../chips/SubstituteChip';
import { NoWrap } from '../ui';
import {
  ForYouSubstitutesQuery,
  ForYouSubstitutesQueryVariables,
} from './__generated__/index.graphql';

const FOR_YOU_SUBSTITUTES_QUERY = gql`
  query ForYouSubstitutesQuery(
    $so5LeagueSlug: String!
    $isIosApp: Boolean!
    $isAndroidApp: Boolean!
    $googlePlayStoreCountryCode: String
  ) {
    forYou {
      unavailablePlayersSubstitutes(limit: 1, so5LeagueSlug: $so5LeagueSlug) {
        substitutes {
          slug
          ...PlayerRecommendationRow_anyPlayer
        }
        unavailablePlayer {
          slug
          displayName
          ...PlayerImg_anyPlayer
        }
      }
    }
  }
  ${PlayerRecommendationRow.fragments.anyPlayer}
  ${PlayerImg.fragments.anyPlayer}
` as TypedDocumentNode<ForYouSubstitutesQuery, ForYouSubstitutesQueryVariables>;

const Extra = styled(Vertical)`
  width: 100%;
  text-align: center;
`;

const NegativeMargin = styled.div`
  margin-top: calc(-1 * var(--triple-unit));
`;

type Props = {
  so5LeagueSlug?: string;
  onStatsClick: (slug: string) => void;
};

export const Substitutes = ({ so5LeagueSlug, onStatsClick }: Props) => {
  const { isIosApp, isAndroidApp, googlePlayStoreCountryCode } =
    useIsMobileApp();
  const { generateSportContextPath } = useSportContext();
  const { data, loading } = useQuery(FOR_YOU_SUBSTITUTES_QUERY, {
    variables: {
      so5LeagueSlug: so5LeagueSlug!,
      isIosApp,
      isAndroidApp,
      googlePlayStoreCountryCode,
    },
    skip: !so5LeagueSlug,
  });

  const unavailablePlayer =
    data?.forYou.unavailablePlayersSubstitutes?.[0]?.unavailablePlayer;

  const players =
    data?.forYou.unavailablePlayersSubstitutes?.[0]?.substitutes.slice(0, 5) ||
    [];

  return (
    <PlayerRecommendationRow
      title={
        <FormattedMessage
          defaultMessage="<noWrap>Substitutes{sorareBallExponent}</noWrap>{br}for {player}"
          id="market.forYou.substitutes.title"
          values={{
            br: <br />,
            noWrap: chunks => <NoWrap>{chunks}</NoWrap>,
            sorareBallExponent: <StarBallExponent />,
            player: unavailablePlayer?.displayName,
          }}
        />
      }
      extra={
        unavailablePlayer && (
          <Extra center gap={2}>
            <ConditionalWrapper
              wrap={!isIosApp}
              Wrapper={Link}
              props={{
                to: generateSportContextPath(ANY_SPORT_PLAYERS_SLUG, {
                  params: { slug: unavailablePlayer.slug },
                }),
              }}
            >
              <PlayerImg
                overlay="linear-gradient(180deg, rgba(211, 169, 236, 0) 0%, rgba(240, 204, 239, 0.25) 50.5%, rgba(238, 235, 240, 0.7) 100%)"
                player={unavailablePlayer}
              />
              <NegativeMargin>
                <Chip
                  size="smaller"
                  outlined
                  label={
                    <FormattedMessage
                      defaultMessage="Unavailable"
                      id="market.forYou.substitutes.unavailable"
                    />
                  }
                />
              </NegativeMargin>
            </ConditionalWrapper>
          </Extra>
        )
      }
      background="linear-gradient(180deg, #D3A9EC 0%, #F0CCEF 50.5%, #EEEBF0 100%);"
      chip={<SubstituteChip />}
      loading={loading}
      players={players}
      onStatsClick={onStatsClick}
      eventProperties={{
        heuristic: ClickMarketTrendsLink_ForYouHeuristic.POPULAR,
        so5LeagueSlug,
      }}
    />
  );
};
